import { useState, useRef, useCallback, useEffect } from "react";
import { ClipboardPaste, FileUp, MessageSquare, Zap, Upload, FileText, Loader2, Briefcase, Heart, Globe, Mic } from "lucide-react";
import { cn } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { runPaidAnalysis } from "@/lib/analyzeMessage";
import { useShieldCredits } from "@/hooks/useShieldCredits";
import PaywallModal from "./PaywallModal";

type InputMode = "paste" | "upload" | "conversation";
type ContextMode = "personal" | "professional" | "public";

const INPUT_MODES = [
  { id: "paste" as const, label: "Paste Message", icon: ClipboardPaste },
  { id: "upload" as const, label: "Upload Document", icon: FileUp },
  { id: "conversation" as const, label: "Import Conversation", icon: MessageSquare },
];

const CONTEXT_MODES = [
  { id: "professional" as const, label: "Professional", icon: Briefcase },
  { id: "personal" as const, label: "Personal", icon: Heart },
  { id: "public" as const, label: "Public", icon: Globe },
];

const PLACEHOLDERS: Record<ContextMode, { paste: string; conversation: string }> = {
  professional: {
    paste: "Paste an email from a colleague, a Slack message, or workplace communication...",
    conversation: "[9:02 AM] Manager: We need to talk about your performance.\n[9:03 AM] You: I'd like to discuss the specifics.\n[9:04 AM] Manager: Everyone else is fine with the changes...",
  },
  personal: {
    paste: "Paste a text from an argument, a DM, or a difficult conversation...",
    conversation: "[12:03] Person A: I never said that.\n[12:04] Person B: Yes you did, I have the messages.\n[12:05] Person A: You're imagining things again...",
  },
  public: {
    paste: "Paste a social media comment, public reply, or online interaction...",
    conversation: "@user1: Your take is completely wrong.\n@user2: Can you explain why?\n@user1: Everyone knows this, you're embarrassing yourself...",
  },
};

const ALLOWED_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
];

interface AnalyzerInputProps {
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
}

const SpeechRecognition = typeof window !== 'undefined'
  ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
  : null;

const useSpeechInput = (onResult: (text: string) => void) => {
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);

  const supported = !!SpeechRecognition;

  const toggle = useCallback(() => {
    if (!SpeechRecognition) return;

    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.continuous = false;
    recognitionRef.current = recognition;

    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      onResult(transcript);
    };
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);

    recognition.start();
    setIsListening(true);
  }, [isListening, onResult]);

  useEffect(() => {
    return () => { recognitionRef.current?.stop(); };
  }, []);

  return { supported, isListening, toggle };
};

const MicButton = ({ isListening, onClick }: { isListening: boolean; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "absolute right-3 top-3 p-1.5 rounded-lg transition-all",
      isListening
        ? "text-cyan animate-pulse-recording bg-cyan/15"
        : "text-muted-foreground hover:text-foreground hover:bg-secondary"
    )}
    title={isListening ? "Stop listening" : "Dictate"}
  >
    <Mic className="w-4 h-4" />
  </button>
);

const RELATIONSHIP_OPTIONS: Record<string, { label: string; options: string[] }> = {
  personal: {
    label: "Who is this with? (optional)",
    options: ["Romantic Partner", "Family", "Friend", "Other"],
  },
  professional: {
    label: "What's the dynamic? (optional)",
    options: ["Manager", "Colleague", "Client", "Other"],
  },
};

const AnalyzerInput = ({ textareaRef }: AnalyzerInputProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [inputMode, setInputMode] = useState<InputMode>("paste");
  const [contextMode, setContextMode] = useState<ContextMode>("personal");
  const [hasSelectedContext, setHasSelectedContext] = useState(false);
  const [relationshipType, setRelationshipType] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [conversation, setConversation] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const pasteMic = useSpeechInput(useCallback((t: string) => setMessage(prev => prev ? prev + ' ' + t : t), []));
  const convMic = useSpeechInput(useCallback((t: string) => setConversation(prev => prev ? prev + ' ' + t : t), []));

  // Reset relationship type when context mode changes
  useEffect(() => {
    setRelationshipType(null);
  }, [contextMode]);

  const { credits, refetch: refetchCredits } = useShieldCredits();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);

  const runEngine = async (text: string, mode: "single" | "conversation") => {
    if (!user) {
      // Signed-out: free browser-side quick scan, no charge, no storage.
      navigate("/analysis-result", {
        state: { message: text, mode, contextMode, relationshipType, quickScan: true },
      });
      return;
    }
    if (credits <= 0) {
      setShowPaywall(true);
      return;
    }
    setIsAnalyzing(true);
    try {
      const result = await runPaidAnalysis(text, contextMode, relationshipType);
      if (result.error || !result.success) {
        const msg = result.error || "Analysis failed";
        if (msg.toLowerCase().includes("credit")) {
          setShowPaywall(true);
        } else {
          toast.error("Analysis failed", { description: msg });
        }
        return;
      }
      refetchCredits();
      navigate("/analysis-result", {
        state: { message: text, mode, contextMode, relationshipType, engineResult: result },
      });
    } catch (err: any) {
      toast.error("Analysis failed", { description: err?.message || "Please try again." });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleAnalyze = () => {
    if (!message.trim() || isAnalyzing) return;
    runEngine(message.trim(), "single");
  };

  const handleConversationAnalyze = () => {
    if (!conversation.trim() || isAnalyzing) return;
    runEngine(conversation.trim(), "conversation");
  };

  const handleFileUpload = useCallback(async (file: File) => {
    if (!user) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error("Unsupported file type", { description: "Please upload PDF, DOCX, or TXT files." });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large", { description: "Maximum file size is 10MB." });
      return;
    }

    setIsUploading(true);
    try {
      const safeName = `${crypto.randomUUID()}${file.name.slice(file.name.lastIndexOf("."))}`;
      const filePath = `${user.id}/${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from("vault-files")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: doc, error: dbError } = await supabase
        .from("vault_documents")
        .insert({
          user_id: user.id,
          file_name: file.name,
          file_path: filePath,
          file_type: file.type,
          file_size: file.size,
        })
        .select("id")
        .single();

      if (dbError) throw dbError;

      setUploadedFile(file.name);
      toast.success("Document uploaded", { description: "Running forensic analysis..." });

      const { error: analyzeError } = await supabase.functions.invoke("analyze-document", {
        body: { documentId: doc.id, filePath },
      });

      if (analyzeError) {
        toast.error("Analysis failed", { description: "Document saved but analysis could not complete." });
      } else {
        toast.success("Analysis complete", { description: "View your evidence report in Reports." });
      }

      navigate("/vault");
    } catch (err: any) {
      toast.error("Upload failed", { description: err.message || "Please try again." });
    } finally {
      setIsUploading(false);
    }
  }, [user, navigate]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  }, [handleFileUpload]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  return (
    <div className="rounded-xl border border-border bg-card p-6 space-y-4">
      <h2 className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-mono">
        Behavioral Intelligence Scanner
      </h2>

      {/* Context Mode Selector */}
      <div className="space-y-2">
        <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground/70 font-mono">
          What type of conversation is this?
        </p>
        <div className="flex items-center gap-2">
          {CONTEXT_MODES.map((ctx) => {
            const isActive = contextMode === ctx.id;
            const Icon = ctx.icon;
            return (
              <button
                key={ctx.id}
                onClick={() => { setContextMode(ctx.id); setHasSelectedContext(true); }}
                className={cn(
                  "flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold transition-all border",
                  isActive
                    ? "bg-cyan/15 text-cyan border-cyan/40 shadow-[0_0_14px_-4px_hsl(184_100%_50%_/_0.35)]"
                    : "bg-transparent text-muted-foreground border-border hover:text-foreground hover:border-muted-foreground/40",
                  !hasSelectedContext && "animate-context-pulse"
                )}
              >
                <Icon className="w-3.5 h-3.5" />
                {ctx.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Relationship Type Selector (optional, context-dependent) */}
      {RELATIONSHIP_OPTIONS[contextMode] && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground/50 font-mono">
            {RELATIONSHIP_OPTIONS[contextMode].label}
          </p>
          <div className="flex flex-wrap items-center gap-1.5">
            {RELATIONSHIP_OPTIONS[contextMode].options.map((opt) => {
              const isActive = relationshipType === opt;
              return (
                <button
                  key={opt}
                  onClick={() => setRelationshipType(isActive ? null : opt)}
                  className={cn(
                    "px-2.5 py-1 rounded-full text-[11px] font-medium transition-all border",
                    isActive
                      ? "bg-cyan/10 text-cyan border-cyan/30"
                      : "bg-transparent text-muted-foreground/60 border-border/50 hover:text-muted-foreground hover:border-border"
                  )}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex items-center gap-1 p-1 rounded-xl bg-secondary/80 border border-border">
        {INPUT_MODES.map((m) => {
          const isActive = inputMode === m.id;
          const Icon = m.icon;
          return (
            <button
              key={m.id}
              onClick={() => setInputMode(m.id)}
              className={cn(
                "flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all",
                isActive
                  ? "bg-cyan/15 text-cyan border border-cyan/30 shadow-[0_0_12px_-4px_hsl(184_100%_50%_/_0.3)]"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              )}
            >
              <Icon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{m.label}</span>
              <span className="sm:hidden">{m.label.split(" ")[0]}</span>
            </button>
          );
        })}
      </div>

      {/* Paste Message Mode */}
      {inputMode === "paste" && (
        <div className="space-y-3">
          <div className="relative">
            <Textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={PLACEHOLDERS[contextMode].paste}
              className="bg-secondary border-border text-foreground placeholder:text-muted-foreground resize-none font-mono text-sm pr-10"
              rows={3}
            />
            {pasteMic.supported && (
              <MicButton isListening={pasteMic.isListening} onClick={pasteMic.toggle} />
            )}
          </div>
          <button
            onClick={handleAnalyze}
            disabled={!message.trim() || isAnalyzing}
            className={cn(
              "flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm",
              "bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground",
              "hover:scale-[1.02] transition-transform",
              "disabled:opacity-50 disabled:pointer-events-none"
            )}
          >
            {isAnalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            {isAnalyzing
              ? "Analyzing..."
              : `${contextMode === "professional" ? "Run Workplace Scan" : contextMode === "public" ? "Run Public Scan" : "Analyze Message"}${user ? " — 1 credit" : ""}`}
          </button>
          {!user && (
            <p className="text-[11px] text-muted-foreground/70 font-mono">
              Quick scan — free preview. Sign in for the full forensic analysis.
            </p>
          )}
        </div>
      )}

      {/* Upload Document Mode */}
      {inputMode === "upload" && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground/70 font-mono">
            {contextMode === "professional"
              ? "Upload HR emails, performance reviews, or legal correspondence."
              : contextMode === "public"
              ? "Upload screenshots or exported comment threads."
              : "Upload legal docs, transcripts, or personal emails for forensic decoding."}
          </p>
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={() => setIsDragging(false)}
            onClick={() => fileInputRef.current?.click()}
            className={cn(
              "flex flex-col items-center justify-center gap-3 py-10 rounded-xl border-2 border-dashed cursor-pointer transition-all",
              isDragging
                ? "border-cyan bg-cyan/5"
                : "border-border hover:border-cyan/40 hover:bg-secondary/50",
              isUploading && "pointer-events-none opacity-60"
            )}
          >
            {isUploading ? (
              <>
                <Loader2 className="w-8 h-8 text-cyan animate-spin" />
                <span className="text-sm text-muted-foreground font-mono">Uploading & analyzing...</span>
              </>
            ) : uploadedFile ? (
              <>
                <FileText className="w-8 h-8 text-emerald-400" />
                <span className="text-sm text-foreground font-mono">{uploadedFile}</span>
                <span className="text-xs text-emerald-400">Uploaded — View in Reports</span>
              </>
            ) : (
              <>
                <Upload className="w-8 h-8 text-muted-foreground" />
                <span className="text-sm text-muted-foreground font-mono">
                  Drop file here or click to browse
                </span>
                <span className="text-[11px] text-muted-foreground/60">
                  PDF, DOCX, TXT — Max 10MB
                </span>
              </>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.txt"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileUpload(file);
            }}
          />
        </div>
      )}

      {/* Import Conversation Mode */}
      {inputMode === "conversation" && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground/70 font-mono">
            {contextMode === "professional"
              ? "Paste a Slack thread, Teams chat, or email chain."
              : contextMode === "public"
              ? "Paste a Twitter/X thread, Reddit chain, or comment section."
              : "Paste a WhatsApp, iMessage, or text conversation."}
          </p>
          <div className="relative">
            <Textarea
              value={conversation}
              onChange={(e) => setConversation(e.target.value)}
              placeholder={PLACEHOLDERS[contextMode].conversation}
              className="bg-secondary border-border text-foreground placeholder:text-muted-foreground resize-none font-mono text-sm pr-10"
              rows={5}
            />
            {convMic.supported && (
              <MicButton isListening={convMic.isListening} onClick={convMic.toggle} />
            )}
          </div>
          <button
            onClick={handleConversationAnalyze}
            disabled={!conversation.trim() || isAnalyzing}
            className={cn(
              "flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm",
              "bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground",
              "hover:scale-[1.02] transition-transform",
              "disabled:opacity-50 disabled:pointer-events-none"
            )}
          >
            {isAnalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
            {isAnalyzing ? "Analyzing..." : `Analyze Conversation${user ? " — 1 credit" : ""}`}
          </button>
        </div>
      )}
      <PaywallModal open={showPaywall} onOpenChange={setShowPaywall} />
    </div>
  );
};

export default AnalyzerInput;
